import React, {createContext, useContext, useMemo} from 'react';

import {useHasReportRuns} from '../../../state/runs/context';
import type * as RunSetTypes from '../../../state/views/runSet/types';
import {useIsNgwEnabled} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {escapeRegexChars} from '../../../util/regex';
import {useMetricKeySearch} from '../../MetricSearch/useMetricKeySearch';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {useMaybeWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {getMaxRunsCeiling} from '../../WorkspaceDrawer/Settings/runLinePlots/utils/getCascadingMaxRuns';
import {isPointVizFullFidelity} from '../../WorkspaceDrawer/Settings/types';
import {runsLinePlotConfigDefaults} from '../defaults';
import {type RunsLinePlotConfig} from '../types';
import {useKeyInfoQueryContext} from './../../../components/MultiRunWorkspace/KeyInfoQueryContext';
import {useIsInLoadedReportContext} from './../../../pages/ReportPage/LoadedReportContext';
import {useLinePlotMaxRunsDynamicConfig} from './../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useRampFlagFullFidelityRanges,
  useRampFlagFullFidelityRunsPerChart,
} from './../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {determineAggregationType} from './useDetermineAggregationType';
import {useFallbackTracking} from './useFallbackTracking';
import {useRunsLinePlotSettings} from './useRunsLinePlotSettings';

type RunsLinePlotContextType = {
  /**
   * Whether the plot is full fidelity or not.
   */
  isFullFidelity: boolean;
  /**
   * Whether the plot is a sampling fallback or not. This is true if the panel _should be in full fidelity_ but some condition (warnings, errors, etc...) is forcing it back to sampling mode
   */
  isSamplingFallback: boolean;
  /**
   * Flag if a render error has occurred. This is currently a bespoke handler for the bucketing query (which does not have full parity with an RSDQ) and any unexpected condition inside that handler will eject the panel to a sampled plot.
   */
  setRenderError: (error: boolean) => void;
  /**
   * Temporary updater to save things to the workspace spec
   */
  updateWorkspaceConfig: (
    config: Partial<RunsLinePlotConfig>,
    options?: {isUndoable?: boolean}
  ) => void;
  /**
   * Granular detail on the configuration settings to display when forcing a fallback to sampling mode. The existence of any messages in this list also is used to control the fallback when we hit known conditions that aren't supported in a bucketed plot
   */
  warningMessages: string[];
  /**
   * Whether the windowing is enabled for the line plot - this only applies to sampled plots currently. Controls whether the data will be stiched together or aggregated over "buckets"
   * e.g. windowing true turns points on different lines into [[1,2]], [[2,4]] => [1.5, 3]
   * e.g. windowing false turns points on different lines into  [[1,2]], [[2,4]] => [[1,2], [2,4]]
   */
  windowing: boolean;
};

/**
 * The goal here is to consolidate the logic for determining the state of the line plot
 *
 * At a high level this will influence whether we do the query through an RSDQ or a b(ucketing)RSDQ, and hopefully in the future these options will feed into the construction of the line itself so that we're not doing a string of memoized mutations all the way down the component tree to manipulate the data (and suffer the render/perf cost of doing so) in order to render the UI.
 */

export const RunsLinePlotContext = createContext<RunsLinePlotContextType>({
  isFullFidelity: false,
  isSamplingFallback: false,
  setRenderError: () => {},
  updateWorkspaceConfig: () => {},
  warningMessages: [],
  windowing: true,
});

const HISTOGRAM_METRIC_TYPES = ['histogram'];
const EMPTY_RUN_SET_REFS: RunSetTypes.Ref[] = [];

/**
 * Whether the panel is plotting a histogram metric.
 *
 * A histogram only renders for a single run (or a report) and a single metric —
 * the renderer draws one heatmap from the first run's history and bails unless
 * exactly one metric is selected. So we only treat the panel as a histogram
 * when it's scoped to one run/report *and* has exactly one metric; anything
 * else (multi-run workspaces, multi-metric or regex panels) can never render a
 * histogram and so isn't forced back to sampling.
 */
export function useIsHistogramMetric(metrics: string[]): boolean {
  const {isSingleRun} = useSingleRunModeContext();
  const hasReportRuns = useHasReportRuns();

  // A histogram renders only for a single metric (yAxis.length === 1 in the
  // renderer), so anything with a different metric count is never a histogram.
  const metric = metrics.length === 1 ? metrics[0] : undefined;
  const isHistogramEligible = (isSingleRun || hasReportRuns) && metric != null;

  const isNgwEnabled = useIsNgwEnabled();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const useMetricApi = isNgwEnabled && workspaceRefs != null;

  const {results: histogramMetricResults} = useMetricKeySearch(
    workspaceRefs?.runSetRefs ?? EMPTY_RUN_SET_REFS,
    workspaceRefs?.customRunNamesRef,
    {
      types: HISTOGRAM_METRIC_TYPES,
      metricNamePattern:
        metric != null ? `^${escapeRegexChars(metric)}$` : undefined,
      pageSize: 1,
      skip: !useMetricApi || !isHistogramEligible,
    }
  );

  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();

  if (!isHistogramEligible || metric == null) {
    return false;
  }

  if (useMetricApi) {
    // metricSearch already filters to histogram-typed metrics, so an exact
    // name match means this metric is a histogram.
    return histogramMetricResults.some(result => result.key === metric);
  }

  const typeCounts =
    historyKeyInfo.keys[metric]?.typeCounts?.flatMap(t => t.type) ?? [];
  return typeCounts.some(type => type === 'histogram');
}

export const RunsLinePlotContextProvider = ({
  children,
  config,
  updateConfig,
}: {
  children: React.ReactNode;
  config: RunsLinePlotConfig;
  updateConfig: (
    config: Partial<RunsLinePlotConfig>,
    options?: {isUndoable?: boolean}
  ) => void;
}) => {
  const [hasRenderError, setRenderError] = React.useState(false);

  const isInReport = useIsInLoadedReportContext();
  const isHistogram = useIsHistogramMetric(config.metrics ?? []);

  const allowFullFidelityRanges = useRampFlagFullFidelityRanges() ?? false;

  const {pointVisualizationMethod} = useRunsLinePlotSettings(config);

  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();
  // NOTE: full-screen params (isFullScreen, canIncreaseFullScreenMaxRuns,
  // dynamicConfigFullScreenMaxRuns) are intentionally omitted here.
  // This ceiling feeds configSupportsBucketing's report-panel check, which
  // forces sampling when limit exceeds the ceiling. Full-screen report
  // panels will use the non-full-screen ceiling — this is a known gap but
  // acceptable for now since full-screen reports are uncommon.
  const maxRunsCeiling = getMaxRunsCeiling({
    canOverrideMaxRuns,
    isBucketing: isPointVizFullFidelity(pointVisualizationMethod),
    isLinePlot: true,
    dynamicConfigMaxRuns,
  });

  const trackFallback = useFallbackTracking({isInReport});
  const {lineType, warningMessages} = determineAggregationType(
    config,
    {
      hasRenderError,
      isInReport,
      pointVisualizationMethod,
      allowFullFidelityRanges,
      maxRunsCeiling,
    },
    trackFallback
  );

  const value = useMemo(
    () => ({
      isFullFidelity: lineType !== 'SAMPLED' && !isHistogram,
      /**
       * A sampling warning shows up under the following conditions:
       * 1. The workspace is in bucketing mode
       * 2. The chart has active sampling warnings that caused the fallback
       */
      isSamplingFallback:
        lineType === 'SAMPLED' && pointVisualizationMethod !== 'sampling',
      setRenderError,
      updateWorkspaceConfig: updateConfig,
      warningMessages,
      // defaults to true
      // https://weightsandbiases.slack.com/archives/C062UKHTAHG/p1725552052483829?thread_ts=1725551993.730519&cid=C062UKHTAHG
      windowing: config.windowing ?? runsLinePlotConfigDefaults.windowing,
    }),
    [
      config.windowing,
      isHistogram,
      lineType,
      pointVisualizationMethod,
      updateConfig,
      warningMessages,
    ]
  );

  return (
    <RunsLinePlotContext.Provider value={value}>
      {children}
    </RunsLinePlotContext.Provider>
  );
};
RunsLinePlotContext.displayName = 'RunsLinePlotContext';

export const useRunsLinePlotContext = () => {
  return useContext(RunsLinePlotContext);
};
